import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Pencil, Briefcase, Check, Loader2, Sparkles } from 'lucide-react';
import { AIAssistantDialog } from '@/components/ai/ai-assistant-dialog';

interface BioSectionProps {
  bio: string;
  tempBio: string;
  isEditing: boolean;
  isSaving: boolean;
  isAIDialogOpen: boolean;
  isAIGenerating: boolean;
  generatingType: string | null;
  aiGeneratedText: string | null;
  onEditClick: () => void;
  onCancel: () => void;
  onSave: () => void;
  onBioChange: (value: string) => void;
  onAIDialogOpenChange: (open: boolean) => void;
  onGenerate: (currentText: string | undefined, options: any) => Promise<string | null>;
  onAccept: (text: string) => void;
  onResetAIData: () => void;
}

export function BioSection({
  bio,
  tempBio,
  isEditing,
  isSaving,
  isAIDialogOpen,
  isAIGenerating,
  generatingType,
  aiGeneratedText,
  onEditClick,
  onCancel,
  onSave,
  onBioChange,
  onAIDialogOpenChange,
  onGenerate,
  onAccept,
  onResetAIData,
}: BioSectionProps) {
  return (
    <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-5 sm:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-3">
          <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-slate-700" />
          <h3 className="text-xl sm:text-2xl text-slate-900">What I Do</h3>
        </div>
        {!isEditing && (
          <button
            onClick={onEditClick}
            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-[48px] border-2 border-slate-200 bg-white text-slate-900 font-normal text-sm sm:text-base hover:bg-slate-50 transition-colors"
          >
            <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
            Edit
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="bio" className="text-sm text-slate-700">Bio</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onAIDialogOpenChange(true)}
              disabled={isAIGenerating}
              className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#2b42a3] hover:bg-[#4669ed]/10"
            >
              {isAIGenerating && generatingType === 'bio' ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-3 w-3 mr-1" />
                  Write bio with AI
                </>
              )}
            </Button>
          </div>
          <AIAssistantDialog
            open={isAIDialogOpen}
            onOpenChange={(open) => {
              onAIDialogOpenChange(open);
              if (!open) {
                onResetAIData();
              }
            }}
            onGenerate={onGenerate}
            onAccept={onAccept}
            generatedText={aiGeneratedText}
            isLoading={isAIGenerating && generatingType === 'bio'}
            type="bio"
            currentText={tempBio}
          />
          
          <Textarea
            id="bio"
            value={tempBio}
            onChange={(e) => onBioChange(e.target.value)}
            className="min-h-[150px] resize-none"
            placeholder="Describe what you do, your expertise, and what value you bring to clients..."
            maxLength={1500}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              {tempBio.length < 100 ? (
                <span>Characters should be between 100 and 1500 ({tempBio.length}/1500)</span>
              ) : (
                <span>{tempBio.length}/1500 characters</span>
              )}
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button 
              variant="outline" 
              onClick={onCancel} 
              disabled={isSaving}
              className="rounded-[48px]"
            >
              Cancel
            </Button>
            <Button 
              onClick={onSave} 
              disabled={isSaving || tempBio.length < 100 || tempBio.length > 1500}
              className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
          {bio || (
            <p className="text-slate-400 italic">
              Tell clients about your expertise, what problems you solve, and what makes you unique...
            </p>
          )}
        </div>
      )}
    </div>
  );
}
